import { useState, useEffect } from 'react';
import { PhoneCall, Save, CheckCircle2 } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, Label, EmptyState, TextArea, TextInput, Button } from '@/components/ui';
import { RAGBadge } from '@/components/Badges';
import { HYPERCARE_DAYS } from '@/lib/constants';
import type { HypercareLog } from '@/types';

const RAG_OPTIONS: ('Red' | 'Amber' | 'Green' | 'Pending')[] = ['Green', 'Amber', 'Red', 'Pending'];

export function HypercareView() {
  const { activeClass, activeClassAgents, updateHypercare } = useStore();
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (activeClassAgents.length > 0 && !selectedAgentId) {
      setSelectedAgentId(activeClassAgents[0].id);
    }
    if (activeClassAgents.length > 0 && selectedAgentId && !activeClassAgents.find((a) => a.id === selectedAgentId)) {
      setSelectedAgentId(activeClassAgents[0].id);
    }
  }, [activeClassAgents, selectedAgentId]);

  if (!activeClass || activeClassAgents.length === 0) {
    return (
      <div className="animate-fade-in">
        <SectionTitle
          title="Hypercare"
          subtitle="Post-training hypercare tracking (3 days, typically Mon–Wed)."
          icon={<PhoneCall className="h-6 w-6" />}
        />
        <EmptyState
          icon={<PhoneCall className="h-8 w-8" />}
          title="No agents to track"
          message="Add agents to a class from the Dashboard to start logging hypercare."
        />
      </div>
    );
  }

  const selectedAgent = activeClassAgents.find((a) => a.id === selectedAgentId);

  function handleUpdate(dayNumber: number, field: keyof HypercareLog, value: string | number | boolean | null) {
    if (!selectedAgent) return;
    const existing = selectedAgent.hypercareLogs.find((h) => h.dayNumber === dayNumber);
    const log: HypercareLog = {
      dayNumber,
      callsTaken: existing?.callsTaken ?? null,
      attendance: existing?.attendance ?? false,
      rag: existing?.rag ?? 'Pending',
      highlights: existing?.highlights ?? '',
      concerns: existing?.concerns ?? '',
      [field]: value,
    } as HypercareLog;
    updateHypercare(selectedAgent.id, log);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  return (
    <div className="animate-fade-in">
      <SectionTitle
        title="Hypercare"
        subtitle={`${HYPERCARE_DAYS} days of post-training hypercare (typically Monday–Wednesday).`}
        icon={<PhoneCall className="h-6 w-6" />}
      />

      {/* Agent selector */}
      <Card className="mb-6 p-5">
        <div className="flex items-end justify-between gap-4">
          <div className="w-full sm:w-80">
            <Label>Select Agent</Label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {activeClassAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.pNumber}
                </option>
              ))}
            </select>
          </div>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm font-medium text-success-600 animate-fade-in">
              <CheckCircle2 className="h-4 w-4" /> Saved!
            </span>
          )}
        </div>
      </Card>

      {/* Hypercare day cards */}
      {selectedAgent && (
        <div className="space-y-6">
          {Array.from({ length: HYPERCARE_DAYS }, (_, i) => i + 1).map((dayNum) => {
            const log = selectedAgent.hypercareLogs.find((h) => h.dayNumber === dayNum);
            return (
              <Card key={dayNum} className="p-6 animate-slide-up">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                    <PhoneCall className="h-5 w-5 text-accent-500" />
                    Hypercare Day {dayNum}
                  </h3>
                  {log && <RAGBadge rag={log.rag === 'Pending' ? 'Amber' : log.rag} size="sm" />}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  {/* Left: calls + attendance + RAG */}
                  <div className="space-y-4">
                    <div>
                      <Label>Calls Taken</Label>
                      <input
                        type="number"
                        min={0}
                        value={log?.callsTaken ?? ''}
                        onChange={(e) =>
                          handleUpdate(dayNum, 'callsTaken', e.target.value === '' ? null : Math.max(0, Number(e.target.value)))
                        }
                        placeholder="—"
                        className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-center text-sm font-semibold text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      />
                    </div>
                    <div>
                      <Label>Attendance</Label>
                      <button
                        type="button"
                        onClick={() => handleUpdate(dayNum, 'attendance', !log?.attendance)}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                          log?.attendance
                            ? 'bg-success-100 text-success-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        <span className={`h-2.5 w-2.5 rounded-full ${log?.attendance ? 'bg-success-500' : 'bg-slate-400'}`} />
                        {log?.attendance ? 'Present' : 'Absent'}
                      </button>
                    </div>
                    <div>
                      <Label>RAG Status</Label>
                      <div className="flex flex-wrap gap-2">
                        {RAG_OPTIONS.map((rag) => (
                          <button
                            key={rag}
                            onClick={() => handleUpdate(dayNum, 'rag', rag)}
                            className={`rounded-lg border-2 px-3 py-1.5 text-xs font-semibold transition-all ${
                              log?.rag === rag
                                ? rag === 'Green'
                                  ? 'border-success-500 bg-success-50 text-success-700'
                                  : rag === 'Amber'
                                  ? 'border-warning-500 bg-warning-50 text-warning-700'
                                  : rag === 'Red'
                                  ? 'border-error-500 bg-error-50 text-error-700'
                                  : 'border-slate-400 bg-slate-100 text-slate-600'
                                : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'
                            }`}
                          >
                            {rag}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: highlights + concerns */}
                  <div className="space-y-4">
                    <div>
                      <Label>Highlights</Label>
                      <TextArea
                        value={log?.highlights ?? ''}
                        onChange={(val) => handleUpdate(dayNum, 'highlights', val)}
                        placeholder="What went well during hypercare?"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Concerns</Label>
                      <TextArea
                        value={log?.concerns ?? ''}
                        onChange={(val) => handleUpdate(dayNum, 'concerns', val)}
                        placeholder="Any issues or concerns?"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
